// public/js/features/tracker-config/tracker-config-page.js
// Manage tracker-config templates: platform templates (superadmin) and tenant
// templates (tenant admins) that extend a platform base. See docs/tracker-config.md.
import { loadTemplates, createTemplate, updateTemplate, deleteTemplate } from './api.js';
import { can, getCurrentUser } from '../../modules/rbac.js';
import { helpIcon, initHelp } from '../../ui/help.js';
import { createBatteryPanel } from './battery-ui.js';
import { BRANDED_TRACKER_FAMILIES } from '../devices/branded-models.js';

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
let DATA = { settings: [], platform: [], tenant: [] };
let isSuper = false;

const helpDict = () => Object.fromEntries(DATA.settings.map(s => [`cfg.${s.key}`, s.help]));
const fmtVal = (s, v) => s.type === 'boolean' ? (v ? 'On' : 'Off') : `${v}${s.unit ? ' ' + s.unit : ''}`;
const baseName = (id) => DATA.platform.find(t => t.templateId === id)?.name || 'unknown base';

// Effective (base-merged) value for each key: own override -> inherited -> default.
const effectiveSettings = (own, inherited) => {
  const eff = {};
  for (const s of DATA.settings) {
    eff[s.key] = own[s.key] !== undefined ? own[s.key]
      : (inherited[s.key] !== undefined ? inherited[s.key] : s.default);
  }
  return eff;
};

// Client-side mirror of validateSettings() in utils/device-config/settings.mjs.
// Keep the thresholds and messages in sync with that file.
const MIN_SCAN_WINDOW_S = 3, STALE_SCAN_INTERVAL_S = 600;
function bleConflicts(eff) {
  const errors = [], warnings = [];
  if (eff.bleScan) {
    const iv = Number(eff.bleScanIntervalS), win = Number(eff.bleScanWindowS);
    if (win >= iv) errors.push(`BLE scan window (${win}s) must be shorter than the scan interval (${iv}s). The tracker cannot scan for longer than the gap between scans.`);
    if (win < MIN_SCAN_WINDOW_S) warnings.push(`A BLE scan window under ${MIN_SCAN_WINDOW_S}s can close before a beacon advertises. Supervised beacons advertise about every 1-3s; if the window ends first, the gateway and the sensor miss each other and the beacon looks absent. Use at least ${MIN_SCAN_WINDOW_S}s.`);
    if (iv > STALE_SCAN_INTERVAL_S) warnings.push(`A ${Math.round(iv / 60)} min scan interval means a beacon can be present for up to that long before the tracker detects it.`);
  }
  return { errors, warnings };
}

export async function initTrackerConfigPage(container) {
  const groups = getCurrentUser().groups || [];
  isSuper = groups.includes('superadmin') || groups.includes('admin') || groups.includes('platformSupport');
  if (!can(groups, 'manageDeviceConfig')) {
    container.innerHTML = `<div class="alert alert-danger m-3">Access denied.</div>`;
    return;
  }
  container.innerHTML = `
    <div class="container-fluid py-3" style="max-width:1000px;">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h1 class="h4 mb-0"><i class="fa-solid fa-sliders me-2"></i>Tracker configuration templates</h1>
        <button id="tc-refresh" class="btn btn-sm btn-outline-secondary"><i class="fa-solid fa-rotate me-1"></i>Refresh</button>
      </div>
      <p class="text-muted small">Templates hold tracker settings. A tenant template extends a platform template and only overrides what it needs. Assign a template to an asset (on the asset page) to push it to the asset's trackers.</p>
      <div id="tc-body"><div class="text-muted py-4"><span class="spinner-border spinner-border-sm me-2"></span>Loading…</div></div>
    </div>`;
  container.querySelector('#tc-refresh').addEventListener('click', () => load(container));
  await load(container);
}

async function load(container) {
  const body = container.querySelector('#tc-body');
  try { DATA = await loadTemplates(); render(container); }
  catch { body.innerHTML = `<div class="alert alert-danger">Failed to load templates.</div>`; }
}

function templateCard(t, editable) {
  const set = t.settings || {};
  const chips = DATA.settings.filter(s => set[s.key] !== undefined)
    .map(s => `<span class="badge text-bg-light border me-1 mb-1">${esc(s.label)}: ${esc(fmtVal(s, set[s.key]))}</span>`).join('')
    || '<span class="text-muted small">No settings — inherits everything.</span>';
  return `
    <div class="card shadow-sm mb-2">
      <div class="card-body py-2">
        <div class="d-flex justify-content-between align-items-start">
          <div>
            <div class="fw-semibold">${esc(t.name)}${t.scope === 'tenant' ? ` <span class="badge text-bg-info ms-1">extends ${esc(baseName(t.baseTemplateId))}</span>` : ''}</div>
            ${t.description ? `<div class="text-muted small">${esc(t.description)}</div>` : ''}
          </div>
          ${editable ? `<div class="text-nowrap">
            <button class="btn btn-sm btn-outline-secondary tc-edit" data-id="${t.templateId}"><i class="fa-solid fa-pen"></i></button>
            <button class="btn btn-sm btn-outline-danger tc-del" data-id="${t.templateId}"><i class="fa-solid fa-trash"></i></button>
          </div>` : ''}
        </div>
        <div class="mt-2">${chips}</div>
      </div>
    </div>`;
}

function render(container) {
  const body = container.querySelector('#tc-body');
  body.innerHTML = `
    <div class="d-flex justify-content-between align-items-center mb-2">
      <h2 class="h6 mb-0 text-secondary">Platform templates</h2>
      ${isSuper ? `<button class="btn btn-sm btn-primary tc-new" data-scope="platform"><i class="fa-solid fa-plus me-1"></i>New platform template</button>` : ''}
    </div>
    <div id="tc-platform">${DATA.platform.length ? DATA.platform.map(t => templateCard(t, isSuper)).join('') : '<div class="text-muted small mb-3">No platform templates yet.</div>'}</div>
    <hr>
    <div class="d-flex justify-content-between align-items-center mb-2 mt-3">
      <h2 class="h6 mb-0 text-secondary">Tenant templates</h2>
      <button class="btn btn-sm btn-primary tc-new" data-scope="tenant" ${DATA.platform.length ? '' : 'disabled title="Create a platform template first"'}><i class="fa-solid fa-plus me-1"></i>New tenant template</button>
    </div>
    <div id="tc-tenant">${DATA.tenant.length ? DATA.tenant.map(t => templateCard(t, true)).join('') : '<div class="text-muted small">No tenant templates yet. Create one from a platform template.</div>'}</div>`;

  body.querySelectorAll('.tc-new').forEach(b => b.addEventListener('click', () => openEditor(container, b.dataset.scope, null)));
  body.querySelectorAll('.tc-edit').forEach(b => b.addEventListener('click', () => {
    const t = [...DATA.platform, ...DATA.tenant].find(x => x.templateId === b.dataset.id);
    openEditor(container, t.scope, t);
  }));
  body.querySelectorAll('.tc-del').forEach(b => b.addEventListener('click', async () => {
    if (!confirm('Delete this template?')) return;
    try { await deleteTemplate(b.dataset.id); await load(container); } catch (e) { window.showToast?.(e.message, 'danger'); }
  }));
}

// ── Editor modal ─────────────────────────────────────────────────────────────
function openEditor(container, scope, template) {
  const editing = !!template;
  const tenantId = (getCurrentUser().tenants?.[0]?.tenantId) || (getCurrentUser().tenantId) || null;
  const state = {
    name: template?.name || '',
    description: template?.description || '',
    baseTemplateId: template?.baseTemplateId || (scope === 'tenant' ? (DATA.platform[0]?.templateId || '') : ''),
    settings: { ...(template?.settings || {}) },   // only the SET keys
  };
  document.getElementById('tcEditor')?.remove();

  const baseOptions = DATA.platform.map(t => `<option value="${t.templateId}"${state.baseTemplateId === t.templateId ? ' selected' : ''}>${esc(t.name)}</option>`).join('');
  const html = `
    <div class="modal fade" id="tcEditor" tabindex="-1"><div class="modal-dialog modal-lg modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">${editing ? 'Edit' : 'New'} ${scope === 'platform' ? 'platform' : 'tenant'} template</h5>
          <button class="btn-close" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body">
          <div class="row g-2 mb-3">
            <div class="col-sm-7"><label class="form-label small fw-semibold">Name</label>
              <input id="tc-name" class="form-control" maxlength="120" value="${esc(state.name)}" placeholder="e.g. In transit"></div>
            ${scope === 'tenant' ? `<div class="col-sm-5"><label class="form-label small fw-semibold">Extends (platform base)</label>
              <select id="tc-base" class="form-select">${baseOptions}</select></div>` : ''}
            <div class="col-12"><label class="form-label small fw-semibold">Description</label>
              <input id="tc-desc" class="form-control" maxlength="1000" value="${esc(state.description)}" placeholder="Optional"></div>
          </div>
          <div class="small text-muted mb-2">${scope === 'tenant'
            ? 'Toggle a setting on to override the base; off = inherit the base value (shown in grey).'
            : 'Toggle a setting on to have this template set it; off = the template leaves it unchanged.'}</div>
          <div id="tc-settings"></div>
          <div id="tc-conflicts" class="mt-1"></div>
          <div id="tc-battery" class="mt-2"></div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
          <button id="tc-save" class="btn btn-primary">${editing ? 'Save' : 'Create'}</button>
        </div>
      </div>
    </div></div>`;
  document.body.insertAdjacentHTML('beforeend', html);
  const el = document.getElementById('tcEditor');
  const modal = new bootstrap.Modal(el);
  el.addEventListener('hidden.bs.modal', () => el.remove(), { once: true });

  const baseSettingsOf = () => (scope === 'tenant'
    ? (DATA.platform.find(t => t.templateId === (el.querySelector('#tc-base')?.value || state.baseTemplateId))?.settings || {})
    : {});

  const battery = createBatteryPanel(el.querySelector('#tc-battery'), {
    families: BRANDED_TRACKER_FAMILIES, familyKey: 'griffinAir', familyLocked: false, hideNotes: true,
  });

  const updateConflicts = (inherited) => {
    const box = el.querySelector('#tc-conflicts');
    if (!box) return;
    const eff = effectiveSettings(state.settings, inherited);
    battery.refresh(eff);
    const { errors, warnings } = bleConflicts(eff);
    box.innerHTML = [
      ...errors.map(m => `<div class="alert alert-danger py-2 px-3 small mb-2"><i class="fa-solid fa-triangle-exclamation me-2"></i>${esc(m)}</div>`),
      ...warnings.map(m => `<div class="alert alert-warning py-2 px-3 small mb-2"><i class="fa-solid fa-circle-info me-2"></i>${esc(m)}</div>`),
    ].join('');
    const save = el.querySelector('#tc-save');
    if (save) { save.disabled = errors.length > 0; save.title = errors.length ? 'Fix the configuration conflict first' : ''; }
  };

  const renderSettings = () => {
    const host = el.querySelector('#tc-settings');
    const inherited = baseSettingsOf();
    const groups = [...new Set(DATA.settings.map(s => s.group))];
    host.innerHTML = groups.map(g => `
      <div class="mb-3">
        <div class="fw-semibold small text-secondary border-bottom pb-1 mb-2">${esc(g)}</div>
        ${DATA.settings.filter(s => s.group === g).map(s => settingRow(s, state.settings, inherited)).join('')}
      </div>`).join('');
    // Wire per-setting include toggles.
    host.querySelectorAll('.tc-inc').forEach(cb => cb.addEventListener('change', () => {
      const key = cb.dataset.key, s = DATA.settings.find(x => x.key === key);
      if (cb.checked) state.settings[key] = state.settings[key] ?? (inherited[key] ?? s.default);
      else delete state.settings[key];
      renderSettings();
    }));
    host.querySelectorAll('.tc-val').forEach(inp => inp.addEventListener('input', () => {
      const key = inp.dataset.key, s = DATA.settings.find(x => x.key === key);
      state.settings[key] = s.type === 'boolean' ? (inp.value === 'true') : Number(inp.value);
      updateConflicts(inherited);
    }));
    initHelp(host, helpDict());
    updateConflicts(inherited);
  };
  renderSettings();
  el.querySelector('#tc-base')?.addEventListener('change', () => { state.baseTemplateId = el.querySelector('#tc-base').value; renderSettings(); });

  el.querySelector('#tc-save').addEventListener('click', async () => {
    const payload = {
      scope,
      name: el.querySelector('#tc-name').value.trim(),
      description: el.querySelector('#tc-desc').value.trim() || null,
      settings: state.settings,
      ...(scope === 'tenant' ? { tenantId, baseTemplateId: el.querySelector('#tc-base').value } : {}),
    };
    if (!payload.name) { window.showToast?.('Name is required', 'warning'); return; }
    const btn = el.querySelector('#tc-save'); btn.disabled = true;
    try {
      if (editing) await updateTemplate(template.templateId, payload); else await createTemplate(payload);
      modal.hide(); window.showToast?.('Template saved', 'success'); await load(container);
    } catch (e) { window.showToast?.(e.message, 'danger'); btn.disabled = false; }
  });
  modal.show();
}

function settingRow(s, own, inherited) {
  const included = own[s.key] !== undefined;
  const inhVal = inherited[s.key];
  const hasInh = inhVal !== undefined;
  const value = included ? own[s.key] : (hasInh ? inhVal : s.default);
  const control = s.type === 'boolean'
    ? `<select class="form-select form-select-sm tc-val" data-key="${s.key}" ${included ? '' : 'disabled'} style="max-width:120px;">
         <option value="true"${value ? ' selected' : ''}>On</option><option value="false"${!value ? ' selected' : ''}>Off</option></select>`
    : `<div class="input-group input-group-sm" style="max-width:170px;">
         <input type="number" class="form-control tc-val" data-key="${s.key}" value="${value}" min="${s.min}" max="${s.max}" ${included ? '' : 'disabled'}>
         ${s.unit ? `<span class="input-group-text">${esc(s.unit)}</span>` : ''}</div>`;
  return `
    <div class="d-flex align-items-center gap-2 mb-2">
      <div class="form-check form-switch mb-0" style="min-width:44px;">
        <input class="form-check-input tc-inc" type="checkbox" data-key="${s.key}" ${included ? 'checked' : ''}>
      </div>
      <div style="flex:1;min-width:0;">
        <span class="small">${esc(s.label)}${helpIcon(`cfg.${s.key}`)}</span>
        ${!included && hasInh ? `<span class="text-muted small ms-2">inherits: ${esc(fmtVal(s, inhVal))}</span>` : ''}
      </div>
      ${control}
    </div>`;
}
